import styled, { css } from "styled-components";

import { BaseButton } from "../Button";
import {
  ANIMATION,
  QUERIES,
  SPACING,
  TOUCH_TARGET,
} from "../../utils/constants";

const SIDEBAR_WIDTH = 232;
const DRAWER_WIDTH = 250;
const BRAND_MARK_SIZE = 44;
const CORNER_RADIUS = SPACING.sm;
const HEADER_Z_INDEX = 1000;
const SIDEBAR_Z_INDEX = 1000;
const BACKDROP_Z_INDEX = 1100;
const DRAWER_Z_INDEX = 1101;

export const ShellWrapper = styled.div`
  min-height: 100vh;
  background: var(--color-background-0);
  color: var(--color-text-default);
`;

export const ShellMobileHeader = styled.header`
  position: sticky;
  top: 0;
  z-index: ${HEADER_Z_INDEX};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${SPACING.md}px;
  padding: ${SPACING.sm}px ${SPACING.md}px;
  border-bottom: 1px solid var(--color-border);
  background: var(--color-card-background);

  @media ${QUERIES.tabletLargeAndUp} {
    display: none;
  }
`;

export const ShellBrand = styled.span`
  font-size: var(--text-base);
  font-weight: 600;
  letter-spacing: -0.01em;
  white-space: nowrap;
`;

export const DrawerBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: ${BACKDROP_Z_INDEX};
  background: var(--color-background-overlay);

  @media ${QUERIES.tabletLargeAndUp} {
    display: none;
  }
`;

export const Sidebar = styled.aside<{ $isOpen: boolean }>`
  position: fixed;
  inset: 0 auto 0 0;
  z-index: ${SIDEBAR_Z_INDEX};
  display: flex;
  flex-direction: column;
  width: ${SIDEBAR_WIDTH}px;
  padding: ${SPACING.lg}px ${SPACING.sm + SPACING.xs}px ${SPACING.md}px;
  overflow-y: auto;
  overscroll-behavior: contain;
  border-right: 1px solid var(--color-border);
  background: var(--color-card-background);

  &:focus {
    outline: none;
  }

  @media ${QUERIES.tabletLargeAndDown} {
    z-index: ${DRAWER_Z_INDEX};
    width: ${DRAWER_WIDTH}px;
    max-width: calc(100vw - ${SPACING.xxl + SPACING.sm}px);
    transform: translateX(${({ $isOpen }) => ($isOpen ? "0" : "-100%")});
    visibility: ${({ $isOpen }) => ($isOpen ? "visible" : "hidden")};
    transition:
      transform ${ANIMATION.duration.normal} ease-out,
      visibility 0s linear
        ${({ $isOpen }) => ($isOpen ? "0s" : ANIMATION.duration.normal)};
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export const DrawerClose = styled.div`
  position: absolute;
  top: ${SPACING.md}px;
  right: ${SPACING.sm + SPACING.xs}px;

  @media ${QUERIES.tabletLargeAndUp} {
    display: none;
  }
`;

export const SidebarBrand = styled.a`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${SPACING.sm + SPACING.xxs}px;
  margin: 0 ${SPACING.sm + SPACING.xs}px ${SPACING.md}px;
  padding-bottom: ${SPACING.lg}px;
  border-bottom: 1px solid var(--color-border);
  color: var(--color-text-default);
  text-decoration: none;

  &:focus-visible {
    outline: 2px solid var(--color-primary);
    outline-offset: 2px;
  }
`;

export const BrandMark = styled.span`
  display: grid;
  place-items: center;
  width: ${BRAND_MARK_SIZE}px;
  height: ${BRAND_MARK_SIZE}px;
  border-radius: ${CORNER_RADIUS}px;
  background: var(--color-primary-color-background);
  color: var(--color-white);
  font-size: var(--text-lg);
  font-weight: 600;
`;

export const BrandCopy = styled.span`
  display: flex;
  flex-direction: column;
  font-size: var(--text-base);
  font-weight: 600;
  line-height: 1.25;
`;

export const BrandCaption = styled.span`
  color: var(--color-text-secondary);
  font-size: var(--text-sm);
  font-weight: 400;
`;

export const SidebarGroup = styled.nav`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xs}px;
`;

export const SidebarFooter = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xs}px;
  margin-top: auto;
  padding-top: ${SPACING.xl}px;
`;

export const SidebarDivider = styled.hr`
  width: calc(100% - ${SPACING.lg}px);
  margin: ${SPACING.sm}px auto;
  border: 0;
  border-top: 1px solid var(--color-border);
`;

const sidebarItem = css`
  display: flex;
  align-items: center;
  flex: none;
  gap: ${SPACING.sm + SPACING.xs}px;
  width: 100%;
  min-height: ${TOUCH_TARGET}px;
  padding: ${SPACING.sm}px ${SPACING.sm + SPACING.xs}px;
  border: 0;
  border-radius: ${CORNER_RADIUS}px;
  background: transparent;
  color: var(--color-text-default);
  font-size: var(--text-base);
  line-height: 1.3;
  text-align: left;
  text-decoration: none;
  transition:
    background ${ANIMATION.duration.fast} ease-out,
    color ${ANIMATION.duration.fast} ease-out;

  &:hover:not(:disabled) {
    background: var(--hover-background);
    color: var(--color-primary);
  }

  &:focus-visible {
    outline: 2px solid var(--color-primary);
    outline-offset: -2px;
  }
`;

export const SidebarLink = styled.a<{ $isActive?: boolean }>`
  ${sidebarItem}

  ${({ $isActive }) =>
    $isActive &&
    css`
      background: color-mix(in srgb, var(--color-primary) 12%, transparent);
      color: var(--color-primary);
      font-weight: 600;
    `}
`;

export const SidebarBadge = styled.span`
  margin-left: auto;
  padding: 0 ${SPACING.xs}px;
  border-radius: 999px;
  background: var(--color-primary-color-background);
  color: var(--color-white);
  font-size: var(--text-sm);
  font-weight: 600;
`;

export const SidebarAction = styled(BaseButton)`
  ${sidebarItem}
`;

export const ShellMain = styled.main`
  margin-left: ${SIDEBAR_WIDTH}px;
  overflow-y: auto;

  @media ${QUERIES.tabletLargeAndDown} {
    margin-left: 0;
  }
`;
